import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, IndianRupee, Play, X } from 'lucide-react';
import axios from 'axios';
import { KeyFeaturesList } from '../components/KeyFeatures';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { RegistrationModal } from '../components/RegistrationModal';
import { useLanguage, useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const priceBadge = (course, tr) => {
  if (course.pricing_mode === 'fee') {
    return `₹${((course.fee_amount || 0) / 100).toLocaleString('en-IN')} · ${tr('Fee')}`;
  }
  if (course.pricing_mode === 'dakshina') return tr('Dakshina');
  return tr('Free');
};

export const Courses = () => {
  usePageTitle('Courses & Teachings');
  const tr = useT();
  const { stateScope } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [regTarget, setRegTarget] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, [stateScope]);

  const fetchCourses = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/courses`, { params: { state: stateScope } });
      setCourses(data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="courses-page">
      <section className="py-24 md:py-32 bg-gradient-to-br from-saffron-50 to-gold-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {tr('Sacred Teachings')}
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {tr('Courses & Teachings')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {tr('Dive deep into the wisdom of Vedic philosophy, Bhagavad Gita, meditation, and the Upanishads.')}
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl font-heading text-charcoal-600">{tr('Loading courses...')}</div>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-charcoal-600">{tr('No courses available at the moment.')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden hover:shadow-[0_12px_40px_rgba(44,30,22,0.12)] transition-all duration-300"
                  data-testid={`course-card-${index}`}
                >
                  <div className="relative">
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-full h-48 object-cover"
                    />
                    {course.preview_video_url && (
                      <button
                        onClick={() => setPreviewVideo(course.preview_video_url)}
                        className="absolute inset-0 bg-black/40 hover:bg-black/60 transition-colors flex items-center justify-center group"
                        data-testid={`preview-button-${index}`}
                      >
                        <div className="w-16 h-16 bg-saffron-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="text-white ml-1" size={28} fill="white" />
                        </div>
                      </button>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="bg-saffron-50 text-saffron-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3">
                      {tr(course.category)}
                    </div>
                    <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-3">
                      {tr(course.title)}
                    </h3>
                    <p className="text-sm text-charcoal-600 leading-relaxed mb-4">
                      {tr(course.description)}
                    </p>
                    {(course.duration || course.level) && (
                      <div className="flex flex-wrap gap-3 items-center text-sm text-charcoal-500 mb-4">
                        {course.duration && (
                          <span className="flex items-center gap-1.5"><Clock size={16} />{tr(course.duration)}</span>
                        )}
                        {course.level && (
                          <span className="inline-block bg-saffron-50 text-saffron-700 px-2 py-0.5 rounded-full text-xs font-medium">{tr(course.level)}</span>
                        )}
                      </div>
                    )}
                    <KeyFeaturesList features={course.key_features} tr={tr} testId={`course-features-${index}`} />
                    <div className="flex items-center justify-between pt-4 border-t border-charcoal-100">
                      <div className="flex items-center gap-1 text-xl font-body font-semibold text-saffron-600">
                        <IndianRupee size={20} />
                        <span className="text-base">{priceBadge(course, tr)}</span>
                      </div>
                      <button
                        onClick={() => setRegTarget({ item: course, kind: 'course' })}
                        data-testid={`course-register-${index}`}
                        className="bg-saffron-600 text-white rounded-full px-6 py-2 text-sm font-body font-medium hover:bg-saffron-500 transition-colors"
                      >
                        {tr('Register Now')}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {previewVideo && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewVideo(null)}
        >
          <button
            onClick={() => setPreviewVideo(null)}
            className="absolute top-6 right-6 text-white hover:text-saffron-400 transition-colors"
            data-testid="close-preview-button"
          >
            <X size={32} />
          </button>
          <div
            className="relative w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={previewVideo}
              title="Course Preview"
              className="w-full h-full rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
      <RegistrationModal
        open={!!regTarget}
        onClose={() => setRegTarget(null)}
        item={regTarget?.item}
        kind={regTarget?.kind}
      />
    </div>
  );
};
